import {
  Button,
  Divider,
  Flex,
  GlobalToken,
  message,
  Select,
  Tag,
  Typography,
} from "antd";
import { ReactNode, useEffect, useState } from "react";
import { useToken } from "../../hooks/useToken";
import { ECountryCode } from "../../models/countries.enum";
import { ELanguage } from "../../models/languages.enum";
import { TProfile } from "../../models/users.model";
import { ThemeSwitcher } from "../../providers/ThemeProvider";
import {
  getUserProfile,
  updateUserProfile,
} from "../../services/users.service";
import { hexToRgba } from "../../utils/hexToRgba";

const getColumns = (token: GlobalToken) => [
  {
    title: "Location & IP",
    dataIndex: "location",
    key: "location",
    render: (text: ReactNode, record: { ip: ReactNode }) => (
      <Flex vertical>
        <Typography.Text
          type="secondary"
          style={{ fontSize: "10px", color: token.colorText }}
        >
          {text}
        </Typography.Text>
        <Typography.Text
          type="secondary"
          style={{ fontSize: "10px", color: token.colorText }}
        >
          {record.ip}
        </Typography.Text>
      </Flex>
    ),
  },
  {
    title: "OS & Browser",
    dataIndex: "osBrowser",
    key: "osBrowser",
    render: (text: ReactNode) => (
      <Typography.Text
        type="secondary"
        style={{
          fontSize: "10px",
          whiteSpace: "pre-line",
          color: token.colorText,
        }}
      >
        {text}
      </Typography.Text>
    ),
  },
  {
    title: "Last accessed",
    dataIndex: "lastAccessed",
    key: "lastAccessed",
    render: (text: ReactNode, record: { created: ReactNode }) => (
      <Flex vertical>
        <Typography.Text
          type="secondary"
          style={{ fontSize: "10px", color: token.colorText }}
        >
          {text}
        </Typography.Text>
        <Typography.Text
          type="secondary"
          style={{ fontSize: "10px", color: token.colorText }}
        >
          {record.created}
        </Typography.Text>
      </Flex>
    ),
  },
  {
    title: "Manage",
    key: "action",
    render: (_: any, record: { action: ReactNode }) =>
      record.action === "Current" ? (
        <Tag style={{ fontSize: token.fontSizeSM, margin: 0 }}>Current</Tag>
      ) : (
        <Tag
          style={{
            fontSize: token.fontSizeSM,
            color: token["red-7"],
            backgroundColor: hexToRgba("#D32029", 0.1) || "",
            border: `1px solid ${token.red9}`,
          }}
        >
          {record.action}
        </Tag>
      ),
  },
];

const data = [
  {
    key: "1",
    location: "São Paulo, Brazil",
    ip: "45.123.132.312",
    osBrowser: "macOS\nChrome",
    lastAccessed: "2 hours ago",
    created: "Created 6 months ago",
    action: "Current",
  },
  {
    key: "2",
    location: "Berlin, Germany",
    ip: "45.123.132.312",
    osBrowser: "macOS\nSafari",
    lastAccessed: "16 days ago",
    created: "Created 2 years ago",
    action: "Sign out",
  },
  {
    key: "3",
    location: "Curitiba, Brazil",
    ip: "45.123.132.312",
    osBrowser: "Windows 11\nFirefox",
    lastAccessed: "6 years ago",
    created: "Created 10 years ago",
    action: "Sign out",
  },
];

export function AccountSettingsBox() {
  const { token } = useToken();
  const tableColumns = getColumns(token);
  const [profile, setProfile] = useState<TProfile>();
  const [isUpdatingCountry, setUpdatingCountry] = useState<boolean>(false);
  const [isUpdatingLanguage, setUpdatingLanguage] = useState<boolean>(false);
  const [isUpdatingDateFormat, setUpdatingDateFormat] =
    useState<boolean>(false);

  useEffect(() => {
    getUserProfile({
      onSuccess: setProfile,
    });
  }, []);

  return (
    <Flex
      style={{
        width: "100%",
        flexDirection: "column",
      }}
    >
      <Flex
        style={{
          flexDirection: "column",
          width: "900px",
          maxWidth: "100%",
          margin: "0 auto",
        }}
      >
        <Typography
          style={{
            fontSize: token.fontSizeHeading4,
          }}
        >
          Account Management
        </Typography>
        <Divider />

        <Flex align="center" style={{ width: "100%" }}>
          <Button
            style={{
              fontSize: "14px",
            }}
          >
            Reset Password
          </Button>
        </Flex>
        <Divider />

        {/* <Flex align="center" justify="space-between" style={{ width: "100%" }}>
          <Typography.Text
            style={{
              fontSize: token.fontSize,
              display: "flex",
              gap: "3px",
              alignItems: "center",
              lineHeight: token.lineHeight,
            }}
          >
            Two-factor Authentication (2FA){" "}
            <FiHelpCircle
              style={{
                color: token.colorTextDescription,
              }}
            />
            :
          </Typography.Text>
          <Switch disabled />
        </Flex>
        <Divider />

        <Flex vertical style={{ width: "100%", marginTop: "10px" }}>
          <Typography
            style={{
              fontSize: token.fontSizeSM,
            }}
          >
            Connected Devices
          </Typography>
          <Table
            className="account-settings-table"
            columns={tableColumns}
            dataSource={data}
            pagination={false}
            style={{ marginTop: token.marginSM }}
          />
        </Flex> */}

        {/* <Divider /> */}

        <Flex
          vertical
          gap={token.marginSM}
          style={{ width: "100%", marginTop: "2%" }}
        >
          <Flex gap="20px">
            <Flex vertical gap="10px" style={{ width: "250px" }}>
              <Typography.Text
                style={{
                  fontSize: token.fontSize,
                }}
              >
                Preferred Language
              </Typography.Text>
              <Select
                showSearch
                defaultValue="English"
                value={profile?.language}
                optionFilterProp="label"
                style={{ width: "100%" }}
                loading={isUpdatingLanguage}
                onChange={(event) => {
                  setUpdatingLanguage(true);
                  updateUserProfile({
                    data: {
                      language: event,
                      lastName: profile?.lastName || "",
                      name: profile?.name || "",
                    },
                    onSuccess: (data) => {
                      setProfile(data);
                      setUpdatingLanguage(false);
                    },
                    onError: () => {
                      setUpdatingLanguage(false);
                      message.error("Error updating language");
                    },
                  });
                }}
                options={Object.keys(ELanguage).map((key) => {
                  return {
                    value: (ELanguage as any)[key],
                    label: key,
                  };
                })}
              />
            </Flex>

            <Flex vertical gap="10px" style={{ width: "250px" }}>
              <Typography.Text
                style={{
                  fontSize: token.fontSize,
                }}
              >
                Country
              </Typography.Text>
              <Select
                style={{ width: "100%" }}
                showSearch
                placeholder="Select a country"
                optionFilterProp="label"
                value={profile?.country}
                loading={isUpdatingCountry}
                onSelect={(value) => {
                  setUpdatingCountry(true);
                  updateUserProfile({
                    data: {
                      language: profile?.language || "en-us",
                      country: value,
                      lastName: profile?.lastName || "",
                      name: profile?.name || "",
                    },
                    onSuccess: (data) => {
                      setProfile(data);
                      setUpdatingCountry(false);
                    },
                    onError: () => {
                      setUpdatingCountry(false);
                      message.error("Error updating country");
                    },
                  });
                }}
                options={Object.keys(ECountryCode).map((key) => {
                  return {
                    value: (ECountryCode as any)[key],
                    label: key[0].toUpperCase() + key.slice(1).toLowerCase(),
                  };
                })}
              />
            </Flex>
          </Flex>
          <Divider />
          <Flex vertical gap="10px" style={{ width: "250px" }}>
            <Typography.Text
              style={{
                fontSize: token.fontSize,
              }}
            >
              Date Format
            </Typography.Text>
            <Select
              defaultValue="DD/MM/YYYY"
              style={{ width: "100%" }}
              value={profile?.dateFormat || "DD/MM/YYYY"}
              loading={isUpdatingDateFormat}
              onChange={(event) => {
                setUpdatingDateFormat(true);
                updateUserProfile({
                  data: {
                    language: profile?.language || "en-us",
                    dateFormat: event,
                    lastName: profile?.lastName || "",
                    name: profile?.name || "",
                    country: profile?.country || "",
                  },
                  onSuccess: (data) => {
                    localStorage.setItem("dateFormat", event);
                    setProfile(data);
                    setUpdatingDateFormat(false);
                  },
                  onError: () => {
                    setUpdatingDateFormat(false);
                    message.error("Error updating date format");
                  },
                });
              }}
            >
              <Select.Option value="DD/MM/YYYY">DD/MM/YYYY</Select.Option>
              <Select.Option value="MM/DD/YYYY">MM/DD/YYYY</Select.Option>
              <Select.Option value="YYYY-MM-DD">YYYY-MM-DD</Select.Option>
            </Select>
          </Flex>
          <Divider />
          <Flex
            vertical
            gap="10px"
            style={{ width: "100%", marginBottom: "15%" }}
          >
            <Typography.Text
              style={{
                fontSize: token.fontSize,
              }}
            >
              Appearance
            </Typography.Text>
            <Flex
              style={{
                width: "20%",
              }}
            >
              <ThemeSwitcher />
            </Flex>
          </Flex>
        </Flex>
        <Divider />

        <Flex vertical style={{ width: "100%" }}>
          <Typography.Title level={5}>
            Delete all data and account
          </Typography.Title>
          <Typography.Text type="secondary">
            All your data will be lost and your account will be deleted. This
            action can't be undone.
          </Typography.Text>
          <Flex style={{ marginTop: token.marginSM }}>
            <Button danger>Delete</Button>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
}
